import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Loader,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  StatusToast,
  WixDesignSystemProvider,
  Image,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { useTranslation } from "react-i18next";
import ClipImage from "./clip.png";
import { ReactTagManager } from "react-gtm-ts";
import * as Icons from "@wix/wix-ui-icons-common";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const BASE_URL = "https://wix.certifiedcode.io/clip/_functions";

function App() {
  const { t } = useTranslation();
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [showReviewToast, setShowReviewToast] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const instance = params.get("instance");

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
  }, []);

  useEffect(() => {
    // Tie PostHog events to the Wix site instance when available.
    identify(instance);
    track("dashboard_opened", { has_token: Boolean(token) });
    loadInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, token]);

  function loadInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: { Authorization: instance || "" },
    })
      .then((response) => response.json())
      .then((data) => {
        setIsUpgraded(data?.instance?.isFree === false);
      })
      .catch(() => {});
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;

  // Surface the review prompt shortly after the page settles (only on the
  // marketing view, not during the install redirect).
  useEffect(() => {
    if (token) return;
    const timer = setTimeout(() => setShowReviewToast(true), 4000);
    return () => clearTimeout(timer);
  }, [token]);

  if (token) {
    track("install_redirect");
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${WIX_APP_ID}&redirectUrl=https://wix.certifiedcode.io/clip/_functions/@certifiedcode/base-backend/auth`;
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle">
              <MarketingPageLayoutContent
                title={t("app_title")}
                content={t("app_content")}
                actions={
                  <Box direction="vertical" gap="SP2">
                    <Button
                      prefixIcon={<Icons.PremiumFilled />}
                      skin={isUpgraded ? "premium-light" : "premium"}
                      as="a"
                      href={upgradeUrl}
                      target="_blank"
                      onClick={() => track("upgrade_clicked")}
                    >
                      {isUpgraded ? t("manage-plan") : t("upgrade")}
                    </Button>
                    <Button
                      priority="secondary"
                      prefixIcon={<Icons.GetStarted />}
                      onClick={() => {
                        track("setup_guide_clicked");
                        window.Intercom("showArticle", 8100023);
                      }}
                    >
                      {t("setup_guide")}
                    </Button>
                    <Button
                      skin="inverted"
                      prefixIcon={<Icons.Reviews />}
                      as="a"
                      href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
                      target="_blank"
                      onClick={() => track("write_review_clicked")}
                    >
                      {t("write_review")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          }
          image={<Image borderRadius={0} src={ClipImage} />}
        />
      </Card>
      {showReviewToast && (
        <StatusToast
          key="review-toast"
          status="info"
          dismissible
          onDismiss={() => setShowReviewToast(false)}
          action={
            <Button
              size="small"
              skin="standard"
              priority="secondary"
              as="a"
              href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
              target="_blank"
              onClick={() => {
                track("review_toast_clicked");
                setShowReviewToast(false);
              }}
            >
              {t("write_review")}
            </Button>
          }
        >
          {t("review_toast")}
        </StatusToast>
      )}
    </WixDesignSystemProvider>
  );
}

export default App;
